import React, { useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Select from '@atlaskit/select/default';
import type { OptionType, ValueType } from '@atlaskit/select/types';

import { asOptionsValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import {
	asFormOptionField,
	toOptions,
} from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { getDropDownMenuPosition } from '../../../../../../../../common/utils';
import {
	findChildValue,
	findParent,
	findParentValue,
} from '../../../../../../../../common/utils/cascading-fields/utils';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormField,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

export const EditCascadingSelectInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue, useFormField);

	const { formatMessage } = useIntl();
	const currentValue = asOptionsValue(useCurrentValue({ fieldId }));
	const formField = useFormField({ fieldId });
	const formOptionField = asFormOptionField(formField);
	const { updateModifiedValue } = useFormRendererActions();
	const isInvalid = useFieldInvalid({ fieldId });
	const isRequired = useFieldRequired({ fieldId });
	const [selectedParentOption, setSelectedParentOption] = useState<OptionType | undefined>(() => {
		if (formOptionField === undefined) {
			return undefined;
		}
		return findParentValue(formOptionField, currentValue) ?? undefined;
	});

	const onChange = useCallback(
		(value: ValueType<OptionType>): void => {
			if (value === null) {
				setSelectedParentOption(undefined);
				updateModifiedValue(fieldId, null);
			} else {
				setSelectedParentOption(value);
				updateModifiedValue(fieldId, {
					options: [
						{
							id: value.value.toString(),
							label: value.label,
						},
					],
				});
			}
		},
		[fieldId, updateModifiedValue],
	);

	const onChangeChild = useCallback(
		(value: ValueType<OptionType>): void => {
			// it is not possible for the child to have no value because it is not clearable.
			if (value !== null && formOptionField !== undefined && selectedParentOption !== undefined) {
				updateModifiedValue(fieldId, {
					options: [
						{
							id: `${
								selectedParentOption.value.toString().split(':')[0]
							}:${value.value.toString()}`,
							// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
							label: `${selectedParentOption.label} - ${value.label}`,
						},
					],
				});
			}
		},
		[selectedParentOption, fieldId, formOptionField, updateModifiedValue],
	);

	if (formOptionField === undefined) {
		return <></>;
	}

	return (
		<Inline testId={`field-input-cascading-select-edit-${fieldId}`} space="space.100">
			<Stack grow="fill">
				<Box>
					<Select<OptionType>
						key={fieldInputId}
						inputId={fieldInputId}
						value={selectedParentOption}
						options={toOptions(formOptionField.options)}
						isSearchable
						noOptionsMessage={() => <FormattedMessage {...messages.optionDropdownNoOptions} />}
						placeholder={<FormattedMessage {...messages.optionDropdownSelect} />}
						{...getDropDownMenuPosition()}
						isInvalid={isInvalid}
						onChange={onChange}
						isClearable
						isRequired={isRequired}
						testId={`field-input-cascading-select-parent-edit-${fieldId}`}
						descriptionId={
							isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
						}
					/>
				</Box>
			</Stack>
			<Stack grow="fill">
				<Box>
					<Select<OptionType>
						key={`${fieldInputId}-child`}
						label={formatMessage(messages.childLabel, { label: formField.label })}
						value={findChildValue(formOptionField, currentValue)}
						// find the current parent option (if any) and then find its children
						options={toOptions(findParent(formOptionField, currentValue)?.children)}
						isSearchable
						noOptionsMessage={() => <FormattedMessage {...messages.optionDropdownNoOptions} />}
						placeholder={<FormattedMessage {...messages.optionDropdownSelect} />}
						{...getDropDownMenuPosition()}
						isInvalid={isInvalid}
						onChange={onChangeChild}
						isRequired={isRequired}
						testId={`field-input-cascading-select-child-edit-${fieldId}`}
						descriptionId={
							isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
						}
					/>
				</Box>
			</Stack>
		</Inline>
	);
};
